"use strict";
/**
 * Schedule Module Types - Shared between backoffice components
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.SOURCE_VISIT_WINDOW_DAYS = exports.SCHEDULE_INSPECTION_TYPES = void 0;
exports.normalizeWorkSource = normalizeWorkSource;
exports.getWorkSourceOptions = getWorkSourceOptions;
exports.getListedScheduleSourceKeys = getListedScheduleSourceKeys;
exports.dedupeSourceAutomation = dedupeSourceAutomation;
exports.getSourceVisitWindow = getSourceVisitWindow;
exports.getScheduleSourceFeatures = getScheduleSourceFeatures;
const reportan_excel_grid_1 = require("./reportan-excel-grid");
exports.SCHEDULE_INSPECTION_TYPES = ['לא נבחר', 'איתור', 'בדק', 'בדק יד 2', 'בדיקת לחות'];
/** Match key for work sources: trimmed, inner whitespace collapsed, case-insensitive. */
function normalizeWorkSource(source) {
    return (source ?? '').trim().replace(/\s+/g, ' ').toLocaleLowerCase();
}
/**
 * The work-source dropdown every visit editor offers: saved closerOptions first, then the
 * built-in DEFAULT_WORK_SOURCE_OPTIONS. Deduplicated by normalizeWorkSource.
 */
function getWorkSourceOptions(config) {
    const options = new Map();
    for (const option of [...(config?.closerOptions ?? []), ...reportan_excel_grid_1.DEFAULT_WORK_SOURCE_OPTIONS]) {
        const key = normalizeWorkSource(option);
        if (key && !options.has(key))
            options.set(key, option.trim().replace(/\s+/g, ' '));
    }
    return [...options.values()];
}
/** Normalized keys of the sources the settings page lists: the dropdown plus saved entries. */
function getListedScheduleSourceKeys(config) {
    return new Set([
        ...getWorkSourceOptions(config).map(normalizeWorkSource),
        ...(config?.sourceAutomation ?? []).map((entry) => normalizeWorkSource(entry.source)),
    ]);
}
const SOURCE_FEATURE_KEYS = ['createLead', 'whatsappReminder', 'smsReminder', 'crmMessages'];
/**
 * One entry per normalized source. When several saved entries match, an explicit off wins
 * per switch, then an explicit on; the first spelling is kept.
 */
function dedupeSourceAutomation(entries) {
    const merged = new Map();
    for (const entry of entries ?? []) {
        const key = normalizeWorkSource(entry.source);
        const current = merged.get(key) ?? { source: entry.source.trim().replace(/\s+/g, ' ') };
        for (const feature of SOURCE_FEATURE_KEYS) {
            if (typeof entry[feature] === 'boolean' && current[feature] !== false)
                current[feature] = entry[feature];
        }
        merged.set(key, current);
    }
    return [...merged.values()];
}
/** Visits that can block or list a source: the window the settings page reads. */
exports.SOURCE_VISIT_WINDOW_DAYS = { past: 90, future: 180 };
/** Israel-date window (YYYY-MM-DD, inclusive) of SOURCE_VISIT_WINDOW_DAYS around now. */
function getSourceVisitWindow(now = new Date()) {
    const today = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Jerusalem' }).format(now);
    const shift = (days) => {
        const date = new Date(`${today}T00:00:00Z`);
        date.setUTCDate(date.getUTCDate() + days);
        return date.toISOString().slice(0, 10);
    };
    return { start: shift(-exports.SOURCE_VISIT_WINDOW_DAYS.past), end: shift(exports.SOURCE_VISIT_WINDOW_DAYS.future) };
}
/**
 * Without a saved switch: a listed source is on, and a blank source (no contractor,
 * Multitan's own visit) is on. A source that is not listed (free text typed on a visit)
 * is off until someone turns it on in settings. A saved switch always wins.
 * adr/default-unlisted-work-sources-to-no-messages.md, adr/send-customer-messages-for-blank-source-visits.md
 */
function getScheduleSourceFeatures(config, source) {
    const key = normalizeWorkSource(source);
    const settings = dedupeSourceAutomation(config?.sourceAutomation?.filter((entry) => normalizeWorkSource(entry.source) === key))[0];
    const messagesByDefault = !key || getListedScheduleSourceKeys(config).has(key);
    return {
        createLead: settings?.createLead !== false,
        whatsappReminder: settings?.whatsappReminder ?? messagesByDefault,
        smsReminder: settings?.smsReminder ?? messagesByDefault,
        crmMessages: settings?.crmMessages ?? messagesByDefault,
    };
}
